import React, { useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Grid,
  Button,
  Paper,
  Skeleton,
  Alert
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  MyLocation as MyLocationIcon
} from '@mui/icons-material';
import { VenueFormData } from '../../../types/Venue';

interface VenueLocationFormProps {
  venueData: VenueFormData;
  updateVenueData: (data: Partial<VenueFormData>) => void;
}

const VenueLocationForm: React.FC<VenueLocationFormProps> = ({ venueData, updateVenueData }) => {
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  
  const handleLocationChange = (field: keyof typeof venueData.location, value: string | number) => {
    const updatedLocation = { ...venueData.location, [field]: value };
    
    updateVenueData({
      location: updatedLocation
    });
  };

  const handleCoordinatesChange = (field: keyof typeof venueData.location.coordinates, value: number) => {
    updateVenueData({
      location: {
        ...venueData.location,
        coordinates: {
          ...venueData.location.coordinates,
          [field]: value
        }
      }
    });
  };
  
  const getCurrentLocation = () => {
    setIsGettingLocation(true);
    setLocationError(null);
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          updateVenueData({
            location: {
              ...venueData.location,
              coordinates: {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude
              }
            }
          });
          setIsGettingLocation(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          setLocationError('Failed to get your current location. Please enter coordinates manually.');
          setIsGettingLocation(false);
        }
      );
    } else {
      setLocationError('Geolocation is not supported by your browser. Please enter coordinates manually.');
      setIsGettingLocation(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Venue Location
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            label="Street Address"
            value={venueData.location.address}
            onChange={(e) => handleLocationChange('address', e.target.value)}
            helperText="Full street address of your venue"
            variant="outlined"
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            label="City"
            value={venueData.location.city}
            onChange={(e) => handleLocationChange('city', e.target.value)}
            variant="outlined"
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            label="State/Province"
            value={venueData.location.state}
            onChange={(e) => handleLocationChange('state', e.target.value)}
            variant="outlined"
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            label="Postal/Zip Code"
            value={venueData.location.postalCode}
            onChange={(e) => handleLocationChange('postalCode', e.target.value)}
            variant="outlined"
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            label="Country"
            value={venueData.location.country}
            onChange={(e) => handleLocationChange('country', e.target.value)}
            variant="outlined"
          />
        </Grid>
      </Grid>
      
      <Box sx={{ mt: 4, mb: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <LocationIcon sx={{ mr: 1 }} />
          Map Coordinates
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          These coordinates will be used to place your venue accurately on the map
        </Typography>
      </Box>
      
      {locationError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {locationError}
        </Alert>
      )}
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Latitude"
                type="number"
                value={venueData.location.coordinates.latitude || ''}
                onChange={(e) => handleCoordinatesChange('latitude', parseFloat(e.target.value))}
                variant="outlined"
                InputProps={{ inputProps: { step: 'any' } }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Longitude"
                type="number"
                value={venueData.location.coordinates.longitude || ''}
                onChange={(e) => handleCoordinatesChange('longitude', parseFloat(e.target.value))}
                variant="outlined"
                InputProps={{ inputProps: { step: 'any' } }}
              />
            </Grid>
          </Grid>
        </Grid>
        
        <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            onClick={getCurrentLocation}
            disabled={isGettingLocation}
            startIcon={<MyLocationIcon />}
            sx={{ height: '56px' }}
          >
            {isGettingLocation ? 'Getting Location...' : 'Use Current Location'}
          </Button>
        </Grid>
      </Grid>
      
      <Paper 
        elevation={1} 
        sx={{ 
          mt: 3, 
          p: 0, 
          height: 250, 
          borderRadius: 1, 
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {(venueData.location.coordinates.latitude && venueData.location.coordinates.longitude) ? (
          <Box
            component="img"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            alt="Location Map Preview"
            src={`https://maps.googleapis.com/maps/api/staticmap?center=${venueData.location.coordinates.latitude},${venueData.location.coordinates.longitude}&zoom=15&size=600x300&maptype=roadmap&markers=color:red%7C${venueData.location.coordinates.latitude},${venueData.location.coordinates.longitude}&key=YOUR_API_KEY`}
          />
        ) : (
          <Box sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body1" align="center" color="text.secondary">
              Enter coordinates or use current location to see map preview
            </Typography>
          </Box>
        )}
        
        {/* Placeholder for when we integrate with actual map provider */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: 'rgba(0,0,0,0.6)',
            color: 'white',
            p: 1,
            textAlign: 'center'
          }}
        >
          Map preview will be shown after venue is created
        </Box>
      </Paper>
    </Box>
  );
};

export default VenueLocationForm;
